import { useCallback } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import type { ISwitcherConfig } from "app/components/React/WidgetContainer/types";
import { allTrackers } from "app/services/track/track";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { isCompareMode } from "app/UtilitiesAndConstants/UtilityFunctions/isCompareMode";

import { ESourceType } from "../../../types";
import useTopNonBrandedKeywordsTable from "../hooks/useTopNonBrandedKeywordsTable";
import { getTopNonBrandedKeywordsColumns } from "../tableColumns";
import { getApiParams, getFooterButtonConfig, getSwitcherItems } from "../utils";

const useTopNonBrandedKeywords = () => {
    const { params } = useCurrentStateAndParams();
    const switcherItems = getSwitcherItems();

    const matchedSwitcherIndex = switcherItems.findIndex((tab) => tab.value === params.selectedNonBrandedTab);
    const selectedSwitcherItem = matchedSwitcherIndex === -1 ? 0 : matchedSwitcherIndex;
    const selectedSwitcherValue = switcherItems[selectedSwitcherItem]?.value;

    const apiParams = getApiParams(selectedSwitcherValue);
    const { data = [], isLoading, isError } = useTopNonBrandedKeywordsTable(apiParams);

    const onSwitcherItemSelect = useCallback(
        (index: number) => {
            const selectedTab = switcherItems[index];
            allTrackers.dispatchTrackEvent("Tab", "click", `Top non-branded keywords/${selectedTab.changeFilter}`);
            proNavigator.applyUpdateParams({ selectedNonBrandedTab: selectedTab.value }, { reload: false });
        },
        [switcherItems],
    );

    const switcherConfig: ISwitcherConfig | undefined =
        data.length > 0
            ? {
                  items: switcherItems,
                  initialSelectedItem: selectedSwitcherItem,
                  onItemClick: onSwitcherItemSelect,
              }
            : undefined;

    const showOrganicVsPaidColumn = selectedSwitcherValue === ESourceType.ALL;
    const columnConfig =
        data?.length > 0 ? getTopNonBrandedKeywordsColumns(showOrganicVsPaidColumn, isCompareMode()) : [];

    const tableProps = {
        data,
        rowHoverable: true,
        isLoading,
        isError,
        columnConfig,
    };

    const selectedFilter = switcherItems[selectedSwitcherItem]?.changeFilter;
    const footerButtonsConfig = getFooterButtonConfig({
        ...params,
        IncludeNoneBranded: true,
        ...(isCompareMode()
            ? { trafficPreset: selectedFilter }
            : { selectedPageTab: selectedFilter === "All" ? "Total" : selectedFilter }),
    });

    return { footerButtonsConfig, tableProps, switcherConfig, onSwitcherItemSelect };
};

export default useTopNonBrandedKeywords;
